/**
 * Enhanced Stripe Configuration System
 * Environment-aware Stripe integration with comprehensive validation
 */

import EnvironmentConfig from "./environment.js";
import FeatureFlags from "./features.js";

export class StripeConfig {
  // Static configuration
  static get config() {
    return {
      publishableKey: process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY,
      // NOTE: The Stripe secret key and webhook secret are intentionally NOT
      // read here. Any VUE_APP_* value is inlined into the client bundle by
      // webpack, so exposing them would leak secrets. They live server-side
      // only, in functions.config().stripe.*.
      environment: process.env.VUE_APP_STRIPE_ENVIRONMENT || "test",
      currency: process.env.VUE_APP_DEFAULT_CURRENCY || "usd",
      country: process.env.VUE_APP_DEFAULT_COUNTRY || "US",
    };
  }

  // Environment-specific API endpoints
  static get apiEndpoints() {
    const baseUrls = {
      local:
        process.env.VUE_APP_API_URL_LOCAL ||
        "http://localhost:5001/tutor-app-5e5eb/us-central1",
      staging:
        process.env.VUE_APP_API_URL_STAGING ||
        "https://us-central1-crescendo-staging-5a0a8.cloudfunctions.net",
      production:
        process.env.VUE_APP_API_URL_PRODUCTION ||
        "https://us-central1-tutor-app-5e5eb.cloudfunctions.net",
    };

    const baseUrl = baseUrls[EnvironmentConfig.environment] || baseUrls.local;

    return {
      createPaymentIntent: `${baseUrl}/createPaymentIntent`,
      createStripeAccount: `${baseUrl}/createStripeAccount`,
      createAccountLink: `${baseUrl}/createAccountLink`,
      getAccountStatus: `${baseUrl}/getAccountStatus`,
      processRefund: `${baseUrl}/processRefund`,
      webhook: `${baseUrl}/stripeWebhook`,
      createCustomer: `${baseUrl}/createCustomer`,
      updateCustomer: `${baseUrl}/updateCustomer`,
      createSubscription: `${baseUrl}/createSubscription`,
      cancelSubscription: `${baseUrl}/cancelSubscription`,
    };
  }

  // Payment configuration
  static get paymentConfig() {
    return {
      // Fee structure
      platformFeePercentage: EnvironmentConfig.payment.platformFeePercentage,
      stripeFeePercentage:
        EnvironmentConfig.payment.stripeProcessingFeePercentage,
      stripeFeeFixed: EnvironmentConfig.payment.stripeProcessingFeeFixed,

      // Payment settings
      currency: this.config.currency.toLowerCase(),
      automaticPaymentMethods:
        process.env.VUE_APP_STRIPE_AUTOMATIC_PAYMENT_METHODS === "true",
      captureMethod: process.env.VUE_APP_STRIPE_CAPTURE_METHOD || "automatic",
      confirmationMethod:
        process.env.VUE_APP_STRIPE_CONFIRMATION_METHOD || "automatic",

      // UI customization
      appearance: {
        theme: process.env.VUE_APP_STRIPE_THEME || "stripe",
        variables: {
          colorPrimary: process.env.VUE_APP_STRIPE_COLOR_PRIMARY || "#0570de",
          colorBackground:
            process.env.VUE_APP_STRIPE_COLOR_BACKGROUND || "#ffffff",
          colorText: process.env.VUE_APP_STRIPE_COLOR_TEXT || "#30313d",
          colorDanger: process.env.VUE_APP_STRIPE_COLOR_DANGER || "#df1b41",
          fontFamily:
            process.env.VUE_APP_STRIPE_FONT_FAMILY ||
            "Ideal Sans, system-ui, sans-serif",
          spacingUnit: process.env.VUE_APP_STRIPE_SPACING_UNIT || "2px",
          borderRadius: process.env.VUE_APP_STRIPE_BORDER_RADIUS || "4px",
        },
      },
    };
  }

  // Webhook configuration
  static get webhookConfig() {
    return {
      // Webhook secret is server-side only (never bundled into the client).
      endpoint: process.env.VUE_APP_STRIPE_WEBHOOK_ENDPOINT,
      events: [
        "payment_intent.succeeded",
        "payment_intent.payment_failed",
        "payment_intent.canceled",
        "charge.succeeded",
        "charge.failed",
        "invoice.payment_succeeded",
        "invoice.payment_failed",
        "customer.subscription.created",
        "customer.subscription.updated",
        "customer.subscription.deleted",
        "account.updated",
        "account.application.deauthorized",
      ],
      tolerance: parseInt(process.env.VUE_APP_STRIPE_WEBHOOK_TOLERANCE) || 300, // 5 minutes
    };
  }

  // Connect/Express account configuration
  static get connectConfig() {
    return {
      // Account creation settings
      type: process.env.VUE_APP_STRIPE_ACCOUNT_TYPE || "express",
      country: this.config.country,

      // Capabilities
      requestedCapabilities: ["card_payments", "transfers"],

      // Account links
      refreshUrl:
        process.env.VUE_APP_STRIPE_REFRESH_URL ||
        `${process.env.VUE_APP_BASE_URL}/stripe/refresh`,
      returnUrl:
        process.env.VUE_APP_STRIPE_RETURN_URL ||
        `${process.env.VUE_APP_BASE_URL}/stripe/return`,

      // Application fee
      applicationFeePercent: this.paymentConfig.platformFeePercentage * 100,

      // Transfer settings
      transferGroup:
        process.env.VUE_APP_STRIPE_TRANSFER_GROUP || "lesson_payments",
    };
  }

  // Mock payment configuration for development
  static get mockConfig() {
    return {
      enabled: FeatureFlags.payments.mockPayments,
      delay: parseInt(process.env.VUE_APP_MOCK_PAYMENT_DELAY) || 2000,
      successRate: parseFloat(process.env.VUE_APP_MOCK_SUCCESS_RATE) || 0.95,
      amounts: {
        min: parseInt(process.env.VUE_APP_MOCK_MIN_AMOUNT) || 500, // $5.00
        max: parseInt(process.env.VUE_APP_MOCK_MAX_AMOUNT) || 50000, // $500.00
      },
    };
  }

  // Validation methods
  static validateConfiguration() {
    const errors = [];
    const warnings = [];
    const info = [];

    // Basic configuration validation
    if (!this.config.publishableKey && !FeatureFlags.payments.mockPayments) {
      errors.push(
        "Stripe publishable key is required when mock payments are disabled"
      );
    }

    if (this.config.publishableKey) {
      // Key format validation
      const isLiveKey = this.config.publishableKey.startsWith("pk_live_");
      const isTestKey = this.config.publishableKey.startsWith("pk_test_");

      if (!isLiveKey && !isTestKey) {
        errors.push("Invalid Stripe publishable key format");
      }

      // Environment-specific key validation
      if (EnvironmentConfig.isProduction && !isLiveKey) {
        warnings.push("Using test Stripe keys in production environment");
      }

      if (!EnvironmentConfig.isProduction && isLiveKey) {
        errors.push(
          "Live Stripe keys detected in non-production environment - security risk"
        );
      }

      info.push(`Stripe key type: ${isLiveKey ? "live" : "test"}`);
    }

    // Webhook secret validation is handled server-side; the frontend never
    // holds the secret, so there is nothing to validate here.

    // Currency validation
    const supportedCurrencies = ["usd", "eur", "gbp", "cad", "aud"];
    if (!supportedCurrencies.includes(this.config.currency.toLowerCase())) {
      warnings.push(
        `Currency ${this.config.currency} may not be supported in all regions`
      );
    }

    // Fee validation
    if (this.paymentConfig.platformFeePercentage > 0.5) {
      warnings.push(
        `High platform fee configured: ${(
          this.paymentConfig.platformFeePercentage * 100
        ).toFixed(1)}%`
      );
    }

    // Mock payment validation
    if (FeatureFlags.payments.mockPayments) {
      if (EnvironmentConfig.isProduction) {
        errors.push("Mock payments are enabled in production environment");
      } else {
        info.push("Mock payments enabled for testing");
      }
    }

    return { errors, warnings, info, isValid: errors.length === 0 };
  }

  // Key type detection
  static getKeyType() {
    if (!this.config.publishableKey) return "none";
    if (this.config.publishableKey.startsWith("pk_live_")) return "live";
    if (this.config.publishableKey.startsWith("pk_test_")) return "test";
    return "invalid";
  }

  // Environment compatibility check
  static isEnvironmentCompatible() {
    const keyType = this.getKeyType();

    if (keyType === "none" && FeatureFlags.payments.mockPayments) {
      return true; // Mock payments allowed
    }

    if (EnvironmentConfig.isProduction && keyType !== "live") {
      return false; // Production requires live keys
    }

    if (!EnvironmentConfig.isProduction && keyType === "live") {
      return false; // Non-production shouldn't use live keys
    }

    return true;
  }

  // Calculate fees
  static calculateFees(amount) {
    const stripeFee = Math.round(
      amount * this.paymentConfig.stripeFeePercentage +
        this.paymentConfig.stripeFeeFixed * 100
    );
    const platformFee = Math.round(
      amount * this.paymentConfig.platformFeePercentage
    );
    const teacherAmount = amount - stripeFee - platformFee;

    return {
      original: amount,
      stripeFee,
      platformFee,
      teacherAmount,
      totalFees: stripeFee + platformFee,
      currency: this.config.currency.toUpperCase(),
    };
  }

  // Format currency amounts
  static formatCurrency(amount, currency = null) {
    const curr = currency || this.config.currency.toUpperCase();
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: curr,
    }).format(amount / 100); // Stripe amounts are in cents
  }

  // Generate client configuration (safe for frontend)
  static getClientConfig() {
    return {
      publishableKey: this.config.publishableKey,
      currency: this.config.currency,
      country: this.config.country,
      environment: this.config.environment,
      appearance: this.paymentConfig.appearance,
      automaticPaymentMethods: this.paymentConfig.automaticPaymentMethods,
      mockPayments: FeatureFlags.payments.mockPayments,
      apiEndpoints: {
        createPaymentIntent: this.apiEndpoints.createPaymentIntent,
        createStripeAccount: this.apiEndpoints.createStripeAccount,
        createAccountLink: this.apiEndpoints.createAccountLink,
        getAccountStatus: this.apiEndpoints.getAccountStatus,
      },
    };
  }

  // Initialize and validate Stripe configuration
  static initialize() {
    const validation = this.validateConfiguration();

    EnvironmentConfig.log("info", "💳 Stripe Configuration:");
    EnvironmentConfig.log("info", `  Environment: ${this.config.environment}`);
    EnvironmentConfig.log("info", `  Key Type: ${this.getKeyType()}`);
    EnvironmentConfig.log(
      "info",
      `  Currency: ${this.config.currency.toUpperCase()}`
    );
    EnvironmentConfig.log(
      "info",
      `  Mock Payments: ${FeatureFlags.payments.mockPayments ? "ON" : "OFF"}`
    );

    // Log validation results
    validation.info.forEach((info) =>
      EnvironmentConfig.log("info", `  ℹ️  ${info}`)
    );

    if (validation.warnings.length > 0) {
      EnvironmentConfig.log("warn", "⚠️  Stripe Warnings:");
      validation.warnings.forEach((warning) =>
        EnvironmentConfig.log("warn", `  - ${warning}`)
      );
    }

    if (validation.errors.length > 0) {
      EnvironmentConfig.log("error", "❌ Stripe Configuration Errors:");
      validation.errors.forEach((error) =>
        EnvironmentConfig.log("error", `  - ${error}`)
      );

      if (EnvironmentConfig.isProduction) {
        throw new Error(
          `Invalid Stripe configuration for production: ${validation.errors.join(
            "; "
          )}`
        );
      }
    }

    if (validation.isValid && validation.warnings.length === 0) {
      EnvironmentConfig.log(
        "info",
        "✅ Stripe configuration validated successfully"
      );
    }

    return validation;
  }

  // Test connection (for development)
  static async testConnection() {
    if (!FeatureFlags.development.testUtilities) {
      throw new Error("Test utilities are disabled in this environment");
    }

    try {
      // This would typically test the Stripe connection
      // For now, we'll just validate the configuration
      const validation = this.validateConfiguration();

      return {
        success: validation.isValid,
        keyType: this.getKeyType(),
        environment: this.config.environment,
        mockPayments: FeatureFlags.payments.mockPayments,
        errors: validation.errors,
        warnings: validation.warnings,
      };
    } catch (error) {
      return {
        success: false,
        error: error.message,
      };
    }
  }

  // Individual calculation methods for convenience
  static calculatePlatformFee(amount) {
    const amountInCents = Math.round(amount * 100);
    return (
      Math.round(amountInCents * this.paymentConfig.platformFeePercentage) / 100
    );
  }

  static calculateProcessingFee(amount) {
    const amountInCents = Math.round(amount * 100);
    const percentage = amountInCents * this.paymentConfig.stripeFeePercentage;
    const fixed = this.paymentConfig.stripeFeeFixed * 100;
    return Math.round(percentage + fixed) / 100;
  }

  static calculateTeacherAmount(amount) {
    const platformFee = this.calculatePlatformFee(amount);
    const processingFee = this.calculateProcessingFee(amount);
    return Math.round((amount - platformFee - processingFee) * 100) / 100;
  }

  // Getter methods for properties used by stripe service
  static get publishableKey() {
    return this.config.publishableKey;
  }

  static get environment() {
    return this.config.environment;
  }

  static get isLive() {
    const key = this.config.publishableKey;
    return key && key.startsWith("pk_live_");
  }
}

export default StripeConfig;
